import { useMemo } from "react";

import {
  BlockedDecisionDetail,
  useBlockedDecisionsQuery,
} from "src/graphql/anti-fraud/queries/alerts/GetBlockedDecisionsQuery";
import { useT } from "src/internationalization";
import { Alert, AlertValidationDecision, AlertValidationDecisionValues } from "src/types/alert";

function toReadablePropertyName(propertyName: string): string {
  return propertyName.replace(/[._]/g, " ").replace(/\s+/g, " ").trim();
}

function buildBlockedReason(
  details: BlockedDecisionDetail[] | undefined,
  t: ReturnType<typeof useT>
): string | undefined {
  if (!details?.length) return undefined;
  const detailsText = details
    .map((detail) => {
      const propertyName = toReadablePropertyName(detail.propertyName);
      return `${propertyName}: ${detail.propertyValue}`;
    })
    .join(", ");
  return t("alerts.confirmFraud.decision.blocked.reason", { details: detailsText });
}

export type UseBlockedByDecisionReturn = {
  blockedByDecision: Map<AlertValidationDecision, boolean>;
  blockedReasonByDecision: Map<AlertValidationDecision, string | undefined>;
  hasBlockedDecisions: boolean;
};

export function useBlockedByDecision(alert: Alert): UseBlockedByDecisionReturn {
  const t = useT();
  const { data: blockedDecisionsData } = useBlockedDecisionsQuery(alert.id);
  const blockedByDecision = useMemo(() => {
    const map = new Map<AlertValidationDecision, boolean>();
    if (!blockedDecisionsData) return map;
    for (const value of AlertValidationDecisionValues) {
      const info = blockedDecisionsData.get(value);
      map.set(value, Boolean(info?.blocked));
    }
    return map;
  }, [blockedDecisionsData]);
  const blockedReasonByDecision = useMemo(() => {
    const map = new Map<AlertValidationDecision, string | undefined>();
    if (!blockedDecisionsData) return map;
    for (const value of AlertValidationDecisionValues) {
      const info = blockedDecisionsData.get(value);
      map.set(value, buildBlockedReason(info?.details, t));
    }
    return map;
  }, [blockedDecisionsData, t]);
  const hasBlockedDecisions = useMemo(
    () => Array.from(blockedByDecision.values()).some(Boolean),
    [blockedByDecision]
  );
  return { blockedByDecision, blockedReasonByDecision, hasBlockedDecisions };
}
